/* ============================================================
   Cuero — Wishlist (localStorage, per-customer scope)
   - Hearts with [data-wishlist-toggle][data-product-handle]
   - Wishlist page container with [data-wishlist-list]
   - Header counter with [data-wishlist-count]
   - Logged-in customers: cuero:wishlist:<customerId>
   - Guests: cuero:wishlist  (merged on login)
   ============================================================ */

(function () {
  const ANON_KEY = 'cuero:wishlist';
  const cuero = window.Cuero || {};
  const customerId = cuero.customerId || null;
  const STORAGE_KEY = customerId ? 'cuero:wishlist:' + customerId : ANON_KEY;
  const LOGIN_NUDGE_KEY = 'cuero:wishlist:nudgeShown';

  function safeParse(raw) {
    try {
      const v = JSON.parse(raw);
      return Array.isArray(v) ? v : [];
    } catch (e) { return []; }
  }

  function read() {
    return safeParse(localStorage.getItem(STORAGE_KEY));
  }

  function write(list) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
  }

  function has(handle) {
    return read().indexOf(handle) !== -1;
  }

  function toggle(handle) {
    const list = read();
    const idx = list.indexOf(handle);
    if (idx === -1) list.push(handle);
    else list.splice(idx, 1);
    write(list);
    document.dispatchEvent(new CustomEvent('cuero:wishlist:change', { detail: { list } }));
    return idx === -1; // true if added
  }

  function mergeAnonymousIntoCustomer() {
    if (!customerId) return;
    const anon = safeParse(localStorage.getItem(ANON_KEY));
    if (anon.length === 0) return;
    const own = read();
    const merged = [];
    own.concat(anon).forEach(function (h) {
      if (h && merged.indexOf(h) === -1) merged.push(h);
    });
    write(merged);
    try { localStorage.removeItem(ANON_KEY); } catch (e) {}
  }

  function paintButton(btn) {
    const handle = btn.getAttribute('data-product-handle');
    if (!handle) return;
    const active = has(handle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  function paintAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(paintButton);
  }

  function paintCount() {
    const count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      el.classList.toggle('is-visible', count > 0);
    });
  }

  // ---------- Login nudge toast (guests only) ----------

  function ensureToastEl() {
    let el = document.querySelector('.cuero-toast');
    if (el) return el;
    el = document.createElement('div');
    el.className = 'cuero-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
    return el;
  }

  function showLoginNudge() {
    if (customerId) return;
    if (sessionStorage.getItem(LOGIN_NUDGE_KEY) === '1') return;
    const loginUrl = cuero.accountLoginUrl || '/account/login';
    const ret = cuero.canonicalUrl ? ('?return_url=' + encodeURIComponent(cuero.canonicalUrl)) : '';
    const el = ensureToastEl();
    el.innerHTML =
      '<div class="cuero-toast__inner">' +
        '<span class="cuero-toast__msg">Saved on this device. <strong>Sign in</strong> to keep your wishlist on your account.</span>' +
        '<a class="cuero-toast__cta" href="' + loginUrl + ret + '">Sign in</a>' +
        '<button type="button" class="cuero-toast__close" aria-label="Dismiss">&times;</button>' +
      '</div>';
    el.classList.add('is-visible');
    sessionStorage.setItem(LOGIN_NUDGE_KEY, '1');

    const close = () => el.classList.remove('is-visible');
    el.querySelector('.cuero-toast__close').addEventListener('click', close);
    setTimeout(close, 6000);
  }

  // ---------- Click handler ----------

  function bindToggles() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      const added = toggle(handle);
      paintButton(btn);
      paintCount();
      if (added && !customerId) showLoginNudge();
      if (document.querySelector('[data-wishlist-list]')) renderWishlistPage();
    });
  }

  // ---------- Wishlist page rendering ----------

  function money(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  function escapeHTML(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHTML(s); }

  function heartSVG() {
    return '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 15.75S1.5 11.25 1.5 6.375C1.5 4.239 3.239 2.5 5.375 2.5c1.36 0 2.557.707 3.625 2.083C10.068 3.207 11.265 2.5 12.625 2.5 14.761 2.5 16.5 4.239 16.5 6.375 16.5 11.25 9 15.75 9 15.75Z" stroke="currentColor" stroke-linejoin="round"/></svg>';
  }

  function cardHTML(p) {
    const img = p.featured_image || (p.images && p.images[0]) || '';
    const url = '/products/' + p.handle;
    const priceVaries = p.price_varies && p.price_min !== undefined && p.price_min !== p.price_max;
    const price = priceVaries ? money(p.price_min) : money(p.price);
    return (
      '<li class="editorial-product wishlist-product">' +
        '<a href="' + url + '" class="editorial-product__media-link" aria-label="' + escapeAttr(p.title) + '">' +
          '<div class="editorial-product__media">' +
            (img ? '<img class="editorial-product__image" loading="lazy" src="' + img + '" alt="' + escapeAttr(p.title) + '">' : '') +
          '</div>' +
        '</a>' +
        '<button type="button" class="editorial-product__wishlist is-active" data-wishlist-toggle data-product-handle="' + escapeAttr(p.handle) + '" aria-pressed="true" aria-label="Remove from wishlist">' +
          heartSVG() +
        '</button>' +
        '<div class="editorial-product__info">' +
          '<a href="' + url + '" class="editorial-product__title-link">' +
            '<h3 class="editorial-product__title">' + escapeHTML(p.title) + '</h3>' +
          '</a>' +
          '<p class="editorial-product__price"><span>' + price + '</span></p>' +
        '</div>' +
      '</li>'
    );
  }

  function renderWishlistPage() {
    const list = document.querySelector('[data-wishlist-list]');
    if (!list) return;
    const empty = document.querySelector('[data-wishlist-empty]');
    const handles = read();

    if (handles.length === 0) {
      list.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    list.innerHTML = '<li class="wishlist-loading">Loading…</li>';
    Promise.all(handles.map(fetchProduct)).then((products) => {
      const found = products.filter(Boolean);
      if (found.length === 0) {
        list.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }
      list.innerHTML = found.map(cardHTML).join('');
    });
  }

  // ---------- Init ----------

  function init() {
    mergeAnonymousIntoCustomer();
    bindToggles();
    paintAllButtons();
    paintCount();
    renderWishlistPage();

    document.addEventListener('cuero:wishlist:change', paintCount);
    document.addEventListener('shopify:section:load', function () {
      paintAllButtons();
      paintCount();
      renderWishlistPage();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Swatch clicks navigate naturally via their <a> href to the
   product page with variant pre-selected. No JS interception needed. */

/* ============================================================
   Cuero — Recently Viewed Products (localStorage)
   - Records product handle on PDP visits
   - Renders recently viewed row on collection pages
   ============================================================ */
(function () {
  var STORAGE_KEY = 'cuero_recently_viewed';
  var MAX_STORED = 12;

  /* --- Track PDP visits --- */
  function recordView() {
    var handle = (window.Cuero || {}).productHandle;
    if (!handle) return;
    var viewed = [];
    try { viewed = JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch (e) { viewed = []; }
    viewed = viewed.filter(function (h) { return h !== handle; });
    viewed.unshift(handle);
    if (viewed.length > MAX_STORED) viewed = viewed.slice(0, MAX_STORED);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(viewed)); } catch (e) {}
  }

  /* --- Render on collection pages --- */
  function formatMoney(cents) {
    var r = (cents / 100).toFixed(2);
    return 'Rs. ' + r.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function renderRecentlyViewed() {
    var section = document.querySelector('[data-recently-viewed-section]');
    if (!section) return;
    var grid = section.querySelector('[data-recently-viewed-grid]');
    var tpl = document.getElementById('recently-viewed-card-template');
    if (!grid || !tpl) return;

    var viewed = [];
    try { viewed = JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch (e) { return; }
    if (!viewed.length) return;

    var exclude = (section.dataset.currentHandles || '').split(',').filter(Boolean);
    var filtered = viewed.filter(function (h) { return exclude.indexOf(h) === -1; });
    var maxShow = parseInt(section.dataset.maxProducts || '4', 10);
    var toShow = filtered.slice(0, maxShow);
    if (!toShow.length) return;

    var fetches = toShow.map(function (h) {
      return fetch('/products/' + h + '.js').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    });

    Promise.all(fetches).then(function (products) {
      var valid = products.filter(Boolean);
      if (!valid.length) return;
      valid.forEach(function (p) {
        var clone = tpl.content.cloneNode(true);
        var card = clone.querySelector('.rv-card');
        var img = clone.querySelector('.rv-card__image img');
        var title = clone.querySelector('.rv-card__title');
        var price = clone.querySelector('.rv-card__price');
        card.href = '/products/' + p.handle;
        img.src = p.featured_image || (p.images && p.images[0]) || '';
        img.alt = p.title;
        title.textContent = p.title;
        price.textContent = formatMoney(p.price);
        grid.appendChild(clone);
      });
      section.style.display = 'block';
    });
  }

  recordView();
  if (document.querySelector('[data-recently-viewed-section]')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', renderRecentlyViewed);
    } else {
      renderRecentlyViewed();
    }
  }
})();

/* ============================================================
   Countdown timer (2026-08-12)
   Markup: snippets/countdown.liquid -> <countdown-timer>
   Used by: sections/countdown-banner.liquid (Sale collection template)
   Ticks .days/.hours/.minutes/.seconds from data-countdown-enddate
   ("YYYY-MM-DDTHH:MM") and data-countdown-timezone ("+0530" = shop
   timezone, empty = visitor local). On end: hides the counter and
   reveals the end message if rendered.
   ============================================================ */
(function () {
  if (window.customElements && customElements.get('countdown-timer')) return;

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      var endRaw = this.getAttribute('data-countdown-enddate') || '';
      var tz = (this.getAttribute('data-countdown-timezone') || '').trim();

      if (!/^\d{4}-\d{2}-\d{2}T\d{1,2}:\d{2}$/.test(endRaw)) return;

      var iso = endRaw + ':00';
      if (tz) {
        iso += tz.slice(0, 3) + ':' + tz.slice(3);
      }
      this.endTime = new Date(iso).getTime();

      this.els = {
        days: this.querySelector('.days'),
        hours: this.querySelector('.hours'),
        minutes: this.querySelector('.minutes'),
        seconds: this.querySelector('.seconds'),
        holder: this.querySelector('.countdown-holder'),
        message: this.querySelector('.countdown-message')
      };

      if (isNaN(this.endTime)) return;

      this.tick();
      this.interval = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    tick() {
      var diff = this.endTime - Date.now();

      if (diff <= 0) {
        diff = 0;
        if (this.interval) clearInterval(this.interval);
        this.classList.add('countdown-ended');
        if (this.els.holder) this.els.holder.classList.add('hide');
        if (this.els.message) this.els.message.classList.remove('hide');
      }

      var total = Math.floor(diff / 1000);
      if (this.els.days) this.els.days.textContent = Math.floor(total / 86400);
      if (this.els.hours) this.els.hours.textContent = Math.floor(total % 86400 / 3600);
      if (this.els.minutes) this.els.minutes.textContent = Math.floor(total % 3600 / 60);
      if (this.els.seconds) this.els.seconds.textContent = total % 60;
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
})();
